import { Dialog, DialogTitle, DialogContent, List, ListItem, ListItemAvatar, Avatar, ListItemText, DialogActions, Button, FormControl, InputLabel, Select, MenuItem, makeStyles, Typography, Box, IconButton, Input } from '@material-ui/core';
import { FC, useContext, useEffect, useState } from 'react';
import AlertContext from '../../store/Alert/AlertContext';
import { MdClose } from 'react-icons/md';
import { appConfig } from '../../config';

const DEFAULT_BLOCKPASS_ID=appConfig.blockpass.id.default
const FATF_BLOCKPASS_ID=appConfig.blockpass.id.fatf

const FATF_COUNTRIES=appConfig.blockpass.countries.fatf
const BLOCKED_COUNTRIES=appConfig.blockpass.countries.blocked

const fatf_countries = FATF_COUNTRIES.split(',').map((each: string) => each.toLowerCase());
const blocked_countries = BLOCKED_COUNTRIES.split(',').map((each: string) => each.toLowerCase());;

type CountryModalProps = {
  open: boolean;
  handleClose: () => void;
  refId: string;
  email: string;
};

interface Country {
    name: {
        common: string,
    },
    flags: {
        svg: string;
    };
}

// Styles for the form control to ensure it takes up full width
const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
      },
    formControl: {
      width: '100%',
    },
    button: {
        height: "61px",
        width: "95%",
        "& .MuiButton-label": {
            fontSize: "1rem",
            fontWeight: 700,
        }
    }
}));
  

const CountryModal: FC<CountryModalProps> = ({ open, handleClose, refId, email }) => {

    const classes = useStyles();
    const { setAlert } = useContext<any>(AlertContext);

    const [countries, setCountries] = useState<any>([]);
    const [blockpass_id, setBlockpassId] = useState<string | null>();
    const [location, setLocation] = useState<string>('');
    const [userLocation, setUserLocation] = useState<string | null>();

    useEffect(() => {
        fetch('https://restcountries.com/v3.1/all?fields=name,flags')
          .then((response) => response.json())
          .then((data: Country[]) => {
            data.sort((a: Country, b: Country) => a.name.common.localeCompare(b.name.common));
            // Map through the data to extract relevant information
            const countriesData = data.map((country: Country) => ({
              name: country.name.common,
              flag: country.flags.svg,
            }));
            setCountries(countriesData);
          });
        
          // Get user location
        fetch('https://ipapi.co/json/')
            .then(response => response.json())
            .then(data => setUserLocation(data.country_name))
    }, []);

    useEffect(() => {
        let blockpass_id;
        if (blocked_countries.includes(location.toLowerCase())) {
            setAlert(`Users from ${location} are not eligible for IDOs. We will try to add support soon.`, 'error');
            setBlockpassId(null);
            return;
        }
        if (fatf_countries.includes(location.toLowerCase())) {
            blockpass_id = FATF_BLOCKPASS_ID;
        } else {
            blockpass_id = DEFAULT_BLOCKPASS_ID;
        }
        setBlockpassId(blockpass_id);
    }, [location]);

    useEffect(() => {
        if (blockpass_id && blockpass_id !== '') {
            const blockpass = new window.BlockpassKYCConnect(
                blockpass_id,
                {
                refId: refId,
                mainColor: '3eb8aa',
                email: email,
                }
            )
        
            blockpass.startKYCConnect();
            blockpass.on('KYCConnectSuccess', () => {
                handleClose();
            })
        }
    }, [blockpass_id])

    useEffect(() => {
        if (userLocation && countries.length > 0) {
            let currentLocation = countries.find((country: any) => {
                return country.name.toLowerCase() === userLocation.toLowerCase()
            })
            if (currentLocation && currentLocation.name)
            setLocation(currentLocation.name);
        }
    }, [userLocation, countries]);

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as string;
        if (value !== '')
        setLocation(value);
    };

    return (
        <Dialog onClose={handleClose} aria-labelledby="country-select-dialog-title" open={open} maxWidth="sm" disableEnforceFocus disableRestoreFocus disablePortal>
            <DialogTitle 
                id="country-select-dialog-title"
            >
                <Box
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: 'center'
                    }}
                >
                    <Typography
                        style={{
                            fontSize: "24px",
                            fontWeight: 700,
                        }}
                    >
                        Issuing Country
                    </Typography>
                    <IconButton onClick={handleClose}>
                        <MdClose />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <form className={classes.container}>
                    <FormControl className={classes.formControl} variant='filled'>
                        <Select
                            labelId="country-select-label"
                            id="country-select"
                            value={location}
                            onChange={handleChange}
                            variant='outlined'
                            style={{
                                padding: "5px 2px"
                            }}
                            MenuProps={{
                                getContentAnchorEl: null, // Prevents the menu from being anchored on the select input
                                PaperProps: {
                                    style: {
                                      maxHeight: 400, // Adjust based on your needs
                                      overflowX: 'hidden',
                                      overflowY: 'auto',
                                    },
                                },    
                                anchorOrigin: {
                                  vertical: "bottom",
                                  horizontal: "left",
                                },
                                transformOrigin: {
                                  vertical: "top",
                                  horizontal: "left",
                                },
                            }}
                        >
                            <MenuItem value="">
                                <em>Select a country</em>
                            </MenuItem>
                            {countries.map((country: any, index: any) => (
                                <MenuItem key={index} value={country.name}>
                                    <img src={country.flag} alt={country.name} style={{ width: 24, marginRight: 5 }} />
                                    {country.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </form>
            </DialogContent>
            <DialogActions>
                <Box
                    style={{
                        textAlign: 'center',
                        justifyContent: 'center',
                        width: "100%",
                        margin: "20px 0px"
                    }}
                >
                    <Button
                        variant="contained" 
                        color="primary" 
                        size="large" 
                        id="blockpass-kyc-connect"
                        disabled={!blockpass_id || blockpass_id === '' || !refId}
                        className={classes.button}
                    >
                        Pass KYC
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default CountryModal;
